import {
  onChangeText,
  setText as nativeSetText,
} from "@-/dev.improve.haniverse.modules.persistent-keyboard";
import { useCallback, useEffect, useState } from "react";

type PersistentKeyboardTextState = {
  /**
   * Highest native `eventCount` processed so far. Held in the same state object as `text` so
   * React always commits the two together — the native `text` prop guard compares this against
   * its own counter, and a count newer than the text it accompanies would let a stale write
   * through, reintroducing the dropped-character bug it exists to prevent.
   */
  mostRecentEventCount: number;
  sourceValue?: string;
  text: string;
};

const INITIAL_STATE: PersistentKeyboardTextState = { mostRecentEventCount: 0, text: "" };

export function usePersistentKeyboardText(): readonly [
  string,
  typeof nativeSetText,
  number,
  string?,
] {
  const [state, setState] = useState<PersistentKeyboardTextState>(INITIAL_STATE);

  useEffect(() => {
    const sub = onChangeText((event) => {
      setState((prev) => {
        // Every change — typed or imperative — now carries the count the native module
        // assigned it, so text and count always describe the same buffer state.
        //
        // `eventCount` is only ever missing if a change reaches us from a source that
        // predates that contract. Reusing `prev.mostRecentEventCount` there would pair new
        // text with an older count, which is exactly the pairing that makes the native guard
        // reject the matching `text` prop write and strand the field out of sync. Treating it
        // as "no newer than what we've seen" keeps the pair monotonic and self-consistent.
        const nextEventCount = event.eventCount ?? prev.mostRecentEventCount;

        // Drop changes that are strictly older than what we've already committed. Native
        // events can be delivered out of order across the bridge, and an older text winning
        // the last write would resurrect characters the user already deleted.
        if (nextEventCount < prev.mostRecentEventCount) {
          return prev;
        }
        if (
          nextEventCount === prev.mostRecentEventCount &&
          event.text === prev.text &&
          event.sourceValue === prev.sourceValue
        ) {
          return prev;
        }
        return {
          mostRecentEventCount: nextEventCount,
          sourceValue: event.sourceValue,
          text: event.text,
        };
      });
    });
    return () => {
      sub.remove();
    };
  }, []);

  const setValue = useCallback((next: string, sourceValue?: string) => {
    // Writes through the native module, which mirrors the change back to every `onChangeText`
    // subscriber (including the one above), so the state update has a single entry point.
    nativeSetText(next, sourceValue);
  }, []);

  return [state.text, setValue, state.mostRecentEventCount, state.sourceValue] as const;
}
